import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];
const PHONE = "387603236229"; // +387 60 323 6229

const WHATSAPP_URL = `https://wa.me/${PHONE}`;

const WhatsAppIcon = ({ size = 56 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" className="drop-shadow-[0_6px_14px_rgba(37,211,102,0.35)]">
    <defs>
      <linearGradient id="wa-grad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#57D163" />
        <stop offset="1" stopColor="#23B33A" />
      </linearGradient>
    </defs>
    <rect x="1" y="1" width="46" height="46" rx="12" fill="url(#wa-grad)" />
    <g transform="translate(11 11) scale(1.083)">
      <path fill="#fff" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885" />
    </g>
  </svg>
);

const ViberIcon = ({ size = 56 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" className="drop-shadow-[0_6px_14px_rgba(115,96,242,0.35)]">
    <rect x="1" y="1" width="46" height="46" rx="12" fill="#7360F2" />
    <g transform="translate(12 12) scale(1)">
      <path fill="#fff" d="M11.4 0C9.473.028 5.333.344 3.02 2.467 1.302 4.187.696 6.7.633 9.817.57 12.933.488 18.776 6.12 20.36h.003l-.004 2.416s-.037.978.61 1.177c.777.242 1.234-.5 1.98-1.302.407-.44.972-1.084 1.397-1.58 3.85.326 6.812-.416 7.15-.526.776-.252 5.176-.816 5.892-6.657.74-6.02-.36-9.83-2.34-11.546l-.006-.003c-.6-.55-3.02-2.31-8.41-2.334 0 0-.396-.026-1.017-.008zm.056 1.694c.526-.004.855.017.855.017 4.562.02 6.748 1.39 7.256 1.85 1.675 1.434 2.53 4.867 1.906 9.892-.6 4.88-4.167 5.19-4.823 5.4-.28.09-2.882.737-6.153.524 0 0-2.436 2.94-3.197 3.704-.12.12-.26.168-.352.144-.13-.033-.166-.187-.165-.412l.02-4.024c-4.762-1.32-4.485-6.292-4.432-8.895.053-2.604.543-4.735 1.996-6.17 1.955-1.774 5.47-2.04 7.263-2.084zM11.9 5.267a.212.212 0 00-.213.21c-.003.117.09.213.207.216 1.267.02 2.31.42 3.117 1.226.79.788 1.196 1.86 1.223 3.32.002.117.1.21.216.208a.213.213 0 00.208-.216c-.028-1.535-.47-2.723-1.35-3.6-.885-.882-2.03-1.328-3.41-1.35a.34.34 0 00-.004 0zm-3.987.42a.723.723 0 00-.423.06l-.005.002c-.29.17-.553.386-.775.65-.185.223-.285.448-.312.667a.615.615 0 00.02.24l.008.005c.245.72.565 1.412.985 2.06.542.99 1.21 1.914 1.994 2.744l.024.033.037.028c.83.784 1.755 1.453 2.746 1.996.647.42 1.34.74 2.06.985l.006.007a.615.615 0 00.24.02c.22-.026.446-.127.668-.312.264-.222.48-.485.65-.775l.003-.005a.723.723 0 00.06-.423c-.024-.14-.104-.26-.24-.34l-1.31-.79c-.14-.083-.29-.077-.42.017l-.005.004c-.19.14-.36.28-.5.42-.08.08-.19.09-.29.03-.5-.28-.94-.65-1.32-1.03-.38-.38-.75-.82-1.03-1.32-.06-.1-.05-.21.03-.29.14-.14.28-.31.42-.5l.004-.005c.094-.13.1-.28.017-.42l-.79-1.31a.42.42 0 00-.34-.24z" />
    </g>
  </svg>
);

export default function FloatingChat() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  const isMobile =
    typeof navigator !== "undefined" &&
    /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
  const VIBER_URL = isMobile
    ? `viber://add?number=${PHONE}`
    : `viber://chat?number=%2B${PHONE}`;

  const options = [
    { key: "whatsapp", label: "WhatsApp", url: WHATSAPP_URL, Icon: WhatsAppIcon },
    { key: "viber", label: "Viber", url: VIBER_URL, Icon: ViberIcon },
  ];

  return (
    <div className="fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-[60] flex flex-col items-end gap-3" data-testid="floating-chat">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.28, ease: EASE }}
            className="flex flex-col items-end gap-3"
          >
            {options.map((o, i) => {
              const isWeb = o.url.startsWith("http");
              return (
                <motion.a
                  key={o.key}
                  href={o.url}
                  {...(isWeb ? { target: "_blank", rel: "noreferrer" } : {})}
                  data-testid={`chat-option-${o.key}`}
                  aria-label={o.label}
                  title={o.label}
                  initial={{ opacity: 0, y: 10, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1, transition: { delay: 0.05 + i * 0.06, ease: EASE } }}
                  className="block transition-transform duration-300 hover:-translate-y-0.5 hover:scale-105"
                >
                  <o.Icon size={56} />
                </motion.a>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle"
        aria-label={t.chat.title}
        aria-expanded={open}
        className="group flex items-center gap-2.5 rounded-3xl bg-mobix-navy py-3.5 pl-4 pr-5 text-white shadow-[0_16px_40px_rgba(11,43,86,0.35)] transition-all duration-300 hover:bg-[#0d3466] hover:-translate-y-0.5"
      >
        <span className="relative flex h-6 w-6 items-center justify-center">
          <AnimatePresence mode="wait" initial={false}>
            {open ? (
              <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                <X size={22} />
              </motion.span>
            ) : (
              <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                <MessageCircle size={22} className="fill-mobix-orange text-mobix-orange" />
              </motion.span>
            )}
          </AnimatePresence>
        </span>
        <span className="text-sm font-bold tracking-wide">{t.chat.label}</span>
      </button>
    </div>
  );
}
